import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { realAnalyticsService } from '../services/realAnalyticsService';
import { useAuth } from '../contexts/AuthContext';

interface QRRedirectProps {}

const QRRedirect: React.FC<QRRedirectProps> = () => {
  const { qrId } = useParams<{ qrId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  // Guard to ensure the redirect/analytics flow runs only once.
  // React StrictMode double-invokes mount effects in development which can
  // cause duplicate analytics/qr_scans inserts. This ref prevents that.
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return; // already ran (dev double-invoke guard)
    hasRunRef.current = true;
    const handleQRRedirect = async () => {
      try {
        if (!qrId) {
          throw new Error('QR ID is required');
        }

        // Get QR code details from database.
        // The URL may contain a human-friendly code (e.g. "QR-MH2TO2SM-...") rather than the
        // internal UUID `id`. Try a flexible lookup by `id` OR `code` to be robust.
        let qrCode: any = null;

        // Helper to detect UUID-like strings so we don't send non-UUID tokens to
        // an `id=eq.<value>` filter (which causes Postgres 22P02 errors).
        const isUuid = (s?: string) => {
          if (!s) return false;
          return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
        };

        try {
          // Only attempt the `id` (UUID) lookup when the token looks like a UUID.
          if (isUuid(qrId)) {
            const res = await supabase
              .from('qr_codes')
              .select('*')
              .eq('id', qrId)
              .limit(1);

            if (res.error) {
              console.warn('QR lookup by id returned error (will try fallback):', res.error);
            }

            if (Array.isArray(res.data) && res.data.length > 0) {
              qrCode = res.data[0];
            }
          }

          // If we didn't resolve by UUID id, try the human-friendly `code` column
          // (case-insensitive), then fall back to `name` for legacy rows.
          if (!qrCode) {
            try {
              const byCode = await supabase
                .from('qr_codes')
                .select('*')
                // Case-insensitive match for code (supports tokens with mixed case)
                .ilike('code', qrId)
                .limit(1);

              if (!byCode.error && Array.isArray(byCode.data) && byCode.data.length > 0) {
                qrCode = byCode.data[0];
              } else {
                // Fall back to searching by `name` for legacy rows that used name as token
                const fallback = await supabase
                  .from('qr_codes')
                  .select('*')
                  .ilike('name', qrId)
                  .limit(1);

                if (fallback.error) {
                  console.warn('QR lookup by name fallback returned error:', fallback.error);
                }

                if (Array.isArray(fallback.data) && fallback.data.length > 0) {
                  qrCode = fallback.data[0];
                }
              }
            } catch (e) {
              console.warn('Error during fallback QR lookup (code/name):', e);
            }
          }
        } catch (lookupErr) {
          console.warn('Exception during QR lookup, continuing to fallback handling:', lookupErr);
        }

        // Extract UTM and tracking parameters from URL
        const trackingParams: { [key: string]: string } = {};
        const utmParams: { [key: string]: string } = {};
        
        searchParams.forEach((value, key) => {
          if (key.startsWith('utm_')) {
            utmParams[key] = value;
          } else if (['qr_id', 'qr_type', 'campaign_id', 'source', 'medium', 'campaign', 'content', 'term'].includes(key)) {
            trackingParams[key] = value;
          }
        });

        // Get user device and location info
        const userAgent = navigator.userAgent;
        const deviceInfo = {
          userAgent,
          platform: navigator.platform,
          language: navigator.language,
          cookieEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine,
          screen: {
            width: window.screen.width,
            height: window.screen.height,
            colorDepth: window.screen.colorDepth
          },
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          }
        };

        // Record analytics and increment QR scan count.
        // Note: IP address fetch removed for performance - it was causing 30-60s timeouts
        // when api.ipify.org was unreachable. The ip_address field is nullable in qr_scans table.
        // We avoid relying on the client's cached `qrCodes` (which may be user-scoped)
        // and instead operate directly with the row we just fetched. If we didn't find
        // a QR row, we'll still attempt to use a `destination` param fallback below.
        if (qrCode) {
          // Insert scan record into qr_scans table (the single source of truth for scans)
          try {
            // Use only columns that exist in the qr_scans table schema:
            // id, qr_code_id, campaign_id, node_id, scan_source, visitor_id,
            // scan_timestamp, user_agent, location_data, device_info, referrer, created_at
            // visitor_id is a persistent per-device ID (localStorage) used for
            // unique-scanner counting — ip_address is unreliable from the browser.
            let visitorId: string | null = null;
            try {
              const VISITOR_KEY = 'lookal_visitor_id';
              visitorId = window.localStorage.getItem(VISITOR_KEY);
              if (!visitorId) {
                visitorId =
                  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
                    ? crypto.randomUUID()
                    : `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
                window.localStorage.setItem(VISITOR_KEY, visitorId);
              }
            } catch (e) {
              visitorId = null; // storage unavailable (private mode)
            }

            // Detect scan source from referrer (direct vs shared link)
            const ref = document.referrer || '';
            const scanSource = /whatsapp|telegram|facebook|twitter|instagram|t\.me|wa\.me/i.test(ref)
              ? 'shared_link'
              : 'direct';

            const scanRow: any = {
              qr_code_id: qrCode.id,
              campaign_id: qrCode.campaign_id ?? null,
              node_id: qrCode.node_id ?? null,
              scan_source: scanSource,
              visitor_id: visitorId,
              user_agent: userAgent,
              scan_timestamp: new Date().toISOString(),
              device_info: deviceInfo,
              referrer: document.referrer || null
            };

            let scanRes = await supabase.from('qr_scans').insert([scanRow]);
            if (scanRes.error) {
              console.warn('qr_scans insert failed, attempting fallback insert:', scanRes.error);
              // Try a minimal insert using columns that commonly exist
              const minimalScan: any = {
                qr_code_id: qrCode.id,
                campaign_id: qrCode.campaign_id ?? null,
                node_id: qrCode.node_id ?? null,
                user_agent: userAgent
              };
              const fallbackRes = await supabase.from('qr_scans').insert([minimalScan]);
              if (fallbackRes.error) {
                console.warn('Minimal qr_scans insertion failed (non-fatal):', fallbackRes.error);
              }
            }
            // Note: total_scans is now calculated dynamically from qr_scans table
            // No need to update total_scans column as it has been removed from the schema
          } catch (trackErr) {
            console.warn('Failed to record QR scan (non-fatal):', trackErr);
          }
        }

        // UTM parameters are already tracked in qr_scans table metadata
        // No need for separate analytics_events tracking

  // Determine destination URL (only from trusted database values)
        let destinationUrl = '';
        
        // Priority order for destination:
        // 1. QR code destination_url (from database - trusted)
        // 2. Campaign view fallback
        // 3. Home/dashboard fallback
        if (qrCode && qrCode.destination_url) {
          destinationUrl = qrCode.destination_url;
        } else if (qrCode && qrCode.campaign_id) {
          // As a safe fallback, link to the campaign's page. We avoid relying on
          // embedded `campaigns` relationship on the QR row (avoids PGRST201 errors).
          destinationUrl = `/campaign/${qrCode.campaign_id}`;
        } else {
          // Final fallback to home/dashboard
          destinationUrl = '/dashboard';
        }

        // Redirect to destination
        if (destinationUrl.startsWith('http')) {
          // External URL
          window.location.href = destinationUrl;
        } else {
          // Internal route
          navigate(destinationUrl);
        }

      } catch (err) {
        console.error('Error handling QR redirect:', err);
        setError(err instanceof Error ? err.message : 'Failed to redirect');
        
        // Fallback to home page after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    handleQRRedirect();
  }, [qrId, searchParams, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Processing QR Code...</h2>
          <p className="text-gray-600">Redirecting you to your destination</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-100">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">QR Code Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default QRRedirect;
